import React, { useState, useEffect } from 'react'
import { useGame } from '../../contexts/GameContext'
import { useAuth } from '../../contexts/AuthContext'
import './MatchmakingDashboard.css'

const TIME_CONTROL_PRESETS = {
  'classical_30_0': {
    label: 'Classical 30+0',
    category: 'Classical',
    description: '30 minutes per side, no increment',
    time: { initial_time: 1800, increment: 0 }
  },
  'rapid_10_0': {
    label: 'Rapid 10+0',
    category: 'Rapid',
    description: '10 minutes per side, no increment',
    time: { initial_time: 600, increment: 0 }
  },
  'blitz_5_2': {
    label: 'Blitz 5+2',
    category: 'Blitz',
    description: '5 minutes per side, 2 seconds increment',
    time: { initial_time: 300, increment: 2 }
  },
  'blitz_3_2': {
    label: 'Blitz 3+2',
    category: 'Blitz',
    description: '3 minutes + 2 seconds increment',
    time: { initial_time: 180, increment: 2 }
  },
  'bullet_1_0': {
    label: 'Bullet 1+0',
    category: 'Bullet',
    description: '1 minute per side, no increment',
    time: { initial_time: 60, increment: 0 }
  },
}

export default function MatchmakingDashboard({ onGameFound }) {
  const { joinQueue, leaveQueue, checkQueueStatus, listGames } = useGame()
  const { user, isGuest } = useAuth()
  
  const [searching, setSearching] = useState(false)
  const [timePreset, setTimePreset] = useState('rapid_10_0')
  const [gameRated, setGameRated] = useState(true)
  const [error, setError] = useState(null)

  // Effect: Force unrated for guests
  useEffect(() => {
    if (isGuest) {
      setGameRated(false)
    }
  }, [isGuest])
  
  // Effect: Check status on mount + Polling while searching
  useEffect(() => {
    let mounted = true
    let pollInterval = null

    const checkStatus = async () => {
        try {
            const res = await checkQueueStatus()
            if (!mounted) return

            if (res && res.in_queue) {
                setSearching(true)
            } else if (searching) {
                // We were searching, but backend says NOT in queue anymore. 
                // Did we find a match? Or get kicked?
                console.log("Matchmaking: No longer in queue. Checking for game...")
                setSearching(false) // Stop UI spinner first
                
                // Refresh games list to see if we have a new active game
                const games = await listGames()
                const newGame = games.find(g => g.status === 'active' || g.status === 'pregame')
                
                if (newGame && onGameFound) {
                    console.log("Matchmaking: Found new game via polling!", newGame.id)
                    onGameFound(newGame.id)
                }
            }
        } catch (err) {
            console.error("Queue status check failed", err)
        }
    }

    // Initial check only if NOT searching (to detect refresh state)
    // If searching, we trust the UI state and wait for interval (prevents race with POST)
    if (!searching) {
        checkStatus()
    }

    // Start polling if searching
    if (searching) {
        pollInterval = setInterval(checkStatus, 3000)
    }

    return () => { 
        mounted = false 
        if (pollInterval) clearInterval(pollInterval)
    }
  }, [checkQueueStatus, searching, listGames, onGameFound])

  const handleQuickMatch = async () => {
    if (searching) return

    try {
      setSearching(true)
      setError(null)
      const selectedPreset = TIME_CONTROL_PRESETS[timePreset]
      // Force unrated for guests
      const isRated = isGuest ? false : gameRated
      await joinQueue(selectedPreset.time, isRated)
    } catch (err) {
      console.error("Matchmaking error:", err)
      setError("Failed to join queue. Please try again.")
      setSearching(false)
    }
  }

  const handleCancel = async () => {
    try {
      await leaveQueue()
    } catch (err) {
      console.error("Leave queue error:", err)
    } finally {
      setSearching(false)
    }
  }

  return (
    <>
      <div className="matchmaking-dashboard-content">

        {searching ? (
           <div className="searching-state">
              <div className="spinner"></div>
              <p>Finding Game...</p>
              <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
           </div>
        ) : (
          <>
            <button
              className="action-button primary"
              onClick={handleQuickMatch}
              disabled={searching}
            >
              Play Now
            </button>

            <div className="time-control-select">
              <label htmlFor="timeControlSelect">Time Control</label>
              <select
                id="timeControlSelect"
                value={timePreset}
                onChange={(e) => setTimePreset(e.target.value)}
                disabled={searching}
              >
                {/* Group time controls by category */}
                {Object.entries(
                  Object.entries(TIME_CONTROL_PRESETS).reduce((groups, [key, preset]) => {
                    const category = preset.category
                    if (!groups[category]) groups[category] = []
                    groups[category].push([key, preset])
                    return groups
                  }, {})
                ).map(([category, presets]) => (
                  <optgroup key={category} label={category}>
                    {presets.map(([key, preset]) => (
                      <option key={key} value={key}>
                        {preset.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <small>{TIME_CONTROL_PRESETS[timePreset]?.description}</small>
            </div>

            <div className="game-type-display">
               <div className="status-badge">
                 {isGuest ? (
                   <span className="badge unrated">Unrated (Guest)</span>
                 ) : (
                   <span className="badge rated">Ranked</span>
                 )}
               </div>
               <small>
                 {isGuest
                   ? 'Guests play unrated games'
                   : 'All matchmaking games are rated'}
               </small>
            </div>
            
             {error && <p className="error-msg">{error}</p>}
          </>
        )}
      </div>
    </>
  )
}
